import { useEffect, useState } from "react";
import { api, downloadFile, blobErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Download } from "lucide-react";

const TYPES = [["access", "Access my data"], ["rectification", "Rectify my data"], ["deletion", "Delete my account"], ["export", "Export my data"]];

export const PrivacyDataCard = () => {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("access");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/me/privacy-requests").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const exportData = async () => {
    try { await downloadFile("/me/export", "airava_my_data.json"); }
    catch (e) { toast.error(await blobErrorDetail(e)); }
  };

  const submit = async () => {
    setBusy(true);
    try { await api.post("/me/privacy-requests", { type, note }); setNote(""); await load(); toast.success("Request submitted — your Workspace Admin will handle it"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); } finally { setBusy(false); }
  };

  return (
    <Card className="p-6 border-slate-200" data-testid="privacy-data-card">
      <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#2563EB]" /><h3 className="text-sm font-semibold text-slate-900">Privacy &amp; data</h3></div>
      <div className="mt-3 flex flex-wrap gap-4 text-sm">
        <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#2563EB] hover:underline" data-testid="account-privacy-link">Privacy Policy</a>
        <a href="/terms" target="_blank" rel="noreferrer" className="text-[#2563EB] hover:underline" data-testid="account-terms-link">Terms of Use</a>
      </div>
      <div className="mt-4">
        <Button variant="outline" onClick={exportData} data-testid="export-my-data-btn"><Download className="h-4 w-4 mr-2" />Download my data (JSON)</Button>
        <p className="text-[11px] text-slate-400 mt-1.5">Includes your profile, consents, personal My Day and saved views. Business data of other users is excluded.</p>
      </div>
      <div className="mt-5 border-t border-slate-100 pt-4">
        <p className="text-xs font-medium text-slate-700">Submit a privacy request</p>
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-9 sm:w-56" data-testid="privacy-request-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea rows={1} className="flex-1 text-sm resize-none" placeholder="Optional details…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="privacy-request-note" />
          <Button onClick={submit} disabled={busy} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="privacy-request-submit">Submit</Button>
        </div>
        {rows.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {rows.map((r) => (
              <div key={r.request_id} className="flex items-center gap-2 text-xs" data-testid={`my-privacy-request-${r.request_id}`}>
                <Badge variant="outline" className="capitalize">{r.type}</Badge>
                <span className="text-slate-500">{new Date(r.created_at).toLocaleDateString()}</span>
                <Badge className="capitalize ml-auto">{r.status.replace(/_/g, " ")}</Badge>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-3">Note: account deletion disables your login but preserves business records (projects, documents, audit history) owned by the workspace.</p>
      </div>
    </Card>
  );
};
